import { createHistoryRouter, createRoute, redirect, type RouteQuery } from 'atomic-router';
import { createEffect, sample } from 'effector';

/*
 * Routes and the history router. The app layer attaches a browser history at startup
 * (`router.setHistory`); tests attach a memory history.
 */
export const routes = {
  home: createRoute(),
  project: createRoute<{ projectId: string }>(),
  /** `?returnTo=/p/…&error=…` */
  signIn: createRoute(),
  /** Magic-link landing page: `?token=…` */
  authVerify: createRoute(),
  /** A read-only share link: `/s/<token>?lang=bg&view=…` */
  shared: createRoute<{ token: string }>(),
  notFound: createRoute(),
};

export const router = createHistoryRouter({
  routes: [
    { path: '/', route: routes.home },
    { path: '/p/:projectId', route: routes.project },
    { path: '/signin', route: routes.signIn },
    { path: '/auth/verify', route: routes.authVerify },
    { path: '/s/:token', route: routes.shared },
  ],
  // Must be a separate route: a matched route that is also the not-found route gets closed.
  notFoundRoute: routes.notFound,
});

redirect({ clock: routes.notFound.opened, route: routes.home, replace: true });

/**
 * Replaces the current URL's query without adding a history entry: view state (tab, selection,
 * value mode) is shareable and survives reloads, but doesn't flood the back button.
 */
export const replaceQueryFx = createEffect(
  ({ path, query }: { path: string; query: RouteQuery }) => ({ path, query }),
);

sample({
  clock: replaceQueryFx.doneData,
  fn: ({ path, query }) => {
    const search = new URLSearchParams(query).toString();
    return {
      path: search ? `${path}?${search}` : path,
      params: {},
      query,
      method: 'replace' as const,
    };
  },
  target: router.push,
});
